import { Button, Card, Col, DatePicker, Radio, Row, Statistic } from "antd";
import type { Dayjs } from "dayjs";
import {
  BellOutlined,
  CalendarOutlined,
  UserAddOutlined,
  UsergroupAddOutlined,
} from "@ant-design/icons";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import React from "react";
import ConsultationRecordApi from "../../apis/consultationRecordApi";
import dayjs from "dayjs";

interface DashboardData {
  todayPendingRevisit: number;
  monthTotal: number;
  monthFirstVisit: number;
  monthRevisit: number;
}


type TimeRangeKey = "本月" | "上月" | "近三个月" | "近六个月" | "今年";

const TIME_RANGE_OPTIONS: TimeRangeKey[] = ["本月", "上月", "近三个月", "近六个月", "今年"];

const FORMAT = "YYYY-MM-DD HH:mm:ss";

const getTimeRange = (key: TimeRangeKey): { startTime: string; endTime: string; label: string } => {
  const now = dayjs();
  switch (key) {
    case "本月":
      return {
        startTime: now.startOf("month").format(FORMAT),
        endTime: now.endOf("month").format(FORMAT),
        label: `${now.year()}年${now.month() + 1}月`,
      };
    case "上月": {
      const last = now.subtract(1, "month");
      return {
        startTime: last.startOf("month").format(FORMAT),
        endTime: last.endOf("month").format(FORMAT),
        label: `${last.year()}年${last.month() + 1}月`,
      };
    }
    case "近三个月":
      return {
        startTime: now.subtract(3, "month").startOf("day").format(FORMAT),
        endTime: now.endOf("day").format(FORMAT),
        label: "近三个月",
      };
    case "近六个月":
      return {
        startTime: now.subtract(6, "month").startOf("day").format(FORMAT),
        endTime: now.endOf("day").format(FORMAT),
        label: "近六个月",
      };
    case "今年":
      return {
        startTime: now.startOf("year").format(FORMAT),
        endTime: now.endOf("year").format(FORMAT),
        label: `${now.year()}年`,
      };
  }
};

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [selectedRange, setSelectedRange] = useState<TimeRangeKey | null>("本月");
  const [customRange, setCustomRange] = useState<[Dayjs | null, Dayjs | null]>([null, null]);
  const [dashboardData, setDashboardData] = useState<DashboardData>({
    todayPendingRevisit: 0,
    monthTotal: 0,
    monthFirstVisit: 0,
    monthRevisit: 0,
  });

  useEffect(() => {
    const userInfo = JSON.parse(sessionStorage.getItem("userInfo") ?? "{}");
    const doctorId: string = userInfo.accountId;
    if (!doctorId) return;

    const fetchRevisitNum = () => {
      ConsultationRecordApi.getDoctorRevisitNum(doctorId).then((res: any) => {
        setDashboardData((prev) => ({ ...prev, todayPendingRevisit: res.data ?? 0 }));
      });
    };

    fetchRevisitNum();
    const timer = setInterval(fetchRevisitNum, 30000);
    return () => clearInterval(timer);
  }, []);

  const fetchDashboard = (startTime: string, endTime: string) => {
    const userInfo = JSON.parse(sessionStorage.getItem("userInfo") ?? "{}");
    const doctorId: string = userInfo.accountId;
    if (!doctorId) return;
    ConsultationRecordApi.getDoctorDashboard(doctorId, startTime, endTime).then((res: any) => {
      const d = res.data ?? {};
      setDashboardData((prev) => ({
        ...prev,
        monthTotal: d.total ?? 0,
        monthFirstVisit: d.firstNum ?? 0,
        monthRevisit: d.revisitNum ?? 0,
      }));
    });
  };

  useEffect(() => {
    const { startTime, endTime } = getTimeRange("本月");
    fetchDashboard(startTime, endTime);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRangeChange = (key: TimeRangeKey) => {
    setSelectedRange(key);
    setCustomRange([null, null]);
    const { startTime, endTime } = getTimeRange(key);
    fetchDashboard(startTime, endTime);
  };

  const handleCustomRangeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    if (dates && dates[0] && dates[1]) {
      setCustomRange(dates);
      setSelectedRange(null);
      const startTime = dates[0].startOf("day").format(FORMAT);
      const endTime = dates[1].endOf("day").format(FORMAT);
      fetchDashboard(startTime, endTime);
    } else {
      setCustomRange([null, null]);
    }
  };

  const getLabel = () => {
    if (selectedRange) return getTimeRange(selectedRange).label;
    if (customRange[0] && customRange[1]) {
      return `${customRange[0].format("YYYY-MM-DD")} 至 ${customRange[1].format("YYYY-MM-DD")}`;
    }
    return "";
  };

  const label = getLabel();

  return (
    <div style={{ padding: 24 }}>
      <h2 style={{ marginBottom: 24 }}>数据概览</h2>

      {/* 今日待复诊提醒 */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          background: dashboardData.todayPendingRevisit > 0 ? "#fffbe6" : "#f6ffed",
          border: `1px solid ${dashboardData.todayPendingRevisit > 0 ? "#ffe58f" : "#b7eb8f"}`,
          borderRadius: 8,
          padding: "16px 24px",
          marginBottom: 32,
          gap: 12,
        }}
      >
        <BellOutlined
          style={{
            fontSize: 24,
            color: dashboardData.todayPendingRevisit > 0 ? "#faad14" : "#52c41a",
          }}
        />
        <div>
          <div style={{ fontSize: 15, fontWeight: 600 }}>今日复诊提醒</div>
          {dashboardData.todayPendingRevisit > 0 ? (
            <div style={{ marginTop: 4, color: "#595959", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <span>
                今日有{" "}
                <span style={{ fontSize: 20, fontWeight: 700, color: "#faad14", margin: "0 4px" }}>
                  {dashboardData.todayPendingRevisit}
                </span>{" "}
                位患者药量即将用尽，请复诊。
              </span>
              <span
                onClick={() => navigate("/home/consultation-record?revisitStatus=PENDING")}
                style={{
                  color: "#faad14",
                  fontWeight: 600,
                  cursor: "pointer",
                  borderBottom: "1px solid #faad14",
                  whiteSpace: "nowrap",
                }}
              >
                点击查看
              </span>
            </div>
          ) : (
            <div style={{ marginTop: 4, color: "#595959" }}>
              今日暂无待复诊患者。
            </div>
          )}
        </div>
      </div>

      {/* 时间筛选 + 统计标题 */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 16,
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div style={{ fontSize: 15, fontWeight: 600, color: "#333" }}>
          {label}看诊统计
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <Radio.Group
            value={selectedRange}
            onChange={(e) => handleRangeChange(e.target.value as TimeRangeKey)}
          >
            {TIME_RANGE_OPTIONS.map((name) => (
              <Radio.Button key={name} value={name}>
                {name}
              </Radio.Button>
            ))}
          </Radio.Group>
          <DatePicker.RangePicker
            value={customRange}
            format="YYYY-MM-DD"
            placeholder={["开始日期", "结束日期"]}
            onChange={(dates) =>
              handleCustomRangeChange(dates as [Dayjs | null, Dayjs | null] | null)
            }
          />
        </div>
      </div>

      {/* 统计卡片 */}
      <Row gutter={16}>
        <Col span={8}>
          <Card
            bordered={false}
            style={{
              borderRadius: 10,
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            }}
          >
            <Statistic
              title={
                <span style={{ color: "rgba(255,255,255,0.85)", fontSize: 14 }}>
                  <CalendarOutlined style={{ marginRight: 6 }} />
                  看诊总数
                </span>
              }
              value={dashboardData.monthTotal}
              suffix="人次"
              valueStyle={{ color: "#fff", fontSize: 32, fontWeight: 700 }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card
            bordered={false}
            style={{
              borderRadius: 10,
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              background: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
            }}
          >
            <Statistic
              title={
                <span style={{ color: "rgba(255,255,255,0.85)", fontSize: 14 }}>
                  <UserAddOutlined style={{ marginRight: 6 }} />
                  初诊人数
                </span>
              }
              value={dashboardData.monthFirstVisit}
              suffix="人"
              valueStyle={{ color: "#fff", fontSize: 32, fontWeight: 700 }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card
            bordered={false}
            style={{
              borderRadius: 10,
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
            }}
          >
            <Statistic
              title={
                <span style={{ color: "rgba(255,255,255,0.85)", fontSize: 14 }}>
                  <UsergroupAddOutlined style={{ marginRight: 6 }} />
                  复诊人数
                </span>
              }
              value={dashboardData.monthRevisit}
              suffix="人"
              valueStyle={{ color: "#fff", fontSize: 32, fontWeight: 700 }}
            />
          </Card>
        </Col>
      </Row>

      {/* 快速跳转 */}
      <div style={{ marginTop: 32, textAlign: "center" }}>
        <Button
          type="primary"
          size="large"
          onClick={() => navigate("/home/consultation-record")}
          style={{
            height: 48,
            padding: "0 48px",
            fontSize: 16,
            fontWeight: 600,
            borderRadius: 8,
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            border: "none",
            boxShadow: "0 4px 12px rgba(102,126,234,0.5)",
          }}
        >
          查看全部问诊记录 →
        </Button>
      </div>
    </div>
  );
};

export default Dashboard;
